import { supabase } from './supabase';
import { CreditService } from './creditService';

interface FlutterwaveConfig {
  publicKey: string;
  secretKey: string;
  currency: string;
  redirectUrl: string;
}

export class SubscriptionService {
  private static config: FlutterwaveConfig = {
    publicKey: process.env.NEXT_PUBLIC_FLUTTERWAVE_PUBLIC_KEY || '',
    secretKey: process.env.FLUTTERWAVE_SECRET_KEY || '',
    currency: 'USD',
    redirectUrl: `${process.env.NEXT_PUBLIC_APP_URL}/dashboard/subscription/callback`
  };

  static async initializePayment(
    userId: string,
    planId: string,
    userEmail: string,
    userName: string,
    currency = 'USD'
  ): Promise<{ checkoutUrl: string; transactionRef: string }> {
    const { data: plan, error: planError } = await supabase
      .from('subscription_plans')
      .select('*')
      .eq('id', planId)
      .single();

    if (planError || !plan) throw new Error('Plan not found');

    const { data: profile } = await supabase
      .from('profiles')
      .select('phone, referral_code')
      .eq('id', userId)
      .single();

    const transactionRef = `BBTUTOR-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    const { data: transaction, error: txError } = await supabase
      .from('transactions')
      .insert({
        user_id: userId,
        plan_id: planId,
        amount: plan.price,
        currency: currency,
        status: 'pending',
        transaction_reference: transactionRef,
        metadata: {
          plan_name: plan.name,
          credits: plan.credits,
          user_email: userEmail,
          user_name: userName
        }
      })
      .select('id')
      .single();

    if (txError) throw txError;

    const payload = {
      tx_ref: transactionRef,
      amount: plan.price,
      currency: currency,
      redirect_url: this.config.redirectUrl,
      customer: {
        email: userEmail,
        name: userName,
        phone_number: profile?.phone || ''
      },
      customizations: {
        title: "BB EdTech - Exam Platform",
        description: `${plan.display_name} - ${plan.credits} credits`,
        logo: `${process.env.NEXT_PUBLIC_APP_URL}/logo.png`
      },
      meta: {
        user_id: userId,
        transaction_id: transaction.id,
        plan_id: planId
      }
    };

    const response = await fetch('https://api.flutterwave.com/v3/payments', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${this.config.secretKey}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    });

    const data = await response.json();

    // FIXED: Proper boolean comparison
    if (data.status !== 'success') {
      await supabase
        .from('transactions')
        .update({ status: 'failed' })
        .eq('id', transaction.id);

      throw new Error(data.message || 'Payment initialization failed');
    }

    return {
      checkoutUrl: data.data.link,
      transactionRef
    };
  }

  static async verifyPayment(transactionId: string, flutterwaveReference: string): Promise<boolean> {
    const response = await fetch(`https://api.flutterwave.com/v3/transactions/${flutterwaveReference}/verify`, {
      headers: {
        'Authorization': `Bearer ${this.config.secretKey}`
      }
    });

    const verification = await response.json();

    if (verification.status === 'success' && verification.data.status === 'successful') {
      const { data: transaction, error: txError } = await supabase
        .from('transactions')
        .update({
          status: 'completed',
          flutterwave_reference: flutterwaveReference,
          updated_at: new Date().toISOString()
        })
        .eq('transaction_reference', transactionId)
        .select('*, subscription_plans(*), profiles(*)')
        .single();

      if (txError || !transaction) throw txError;

      const userId = transaction.user_id;
      const plan = transaction.subscription_plans;

      await CreditService.addCredits(userId, plan.credits, {
        type: 'subscription',
        description: `Purchased ${plan.display_name}`,
        referenceId: transaction.id
      });

      await supabase
        .from('profiles')
        .update({
          subscription_tier: plan.name,
          subscription_ends_at: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
          auto_renew: true
        })
        .eq('id', userId);

      await this.processReferralBonus(userId, transaction.id);

      return true;
    }

    return false;
  }

  private static async processReferralBonus(userId: string, transactionId: string): Promise<void> {
    const { data: referral, error } = await supabase
      .from('referrals')
      .select('*')
      .eq('referred_id', userId)
      .eq('status', 'pending')
      .single();

    if (error || !referral) return;

    const referrerId = referral.referrer_id;

    await CreditService.addCredits(referrerId, 5, {
      type: 'referral',
      description: `Referral bonus for ${referral.referred_id}`,
      referenceId: transactionId
    });

    await supabase
      .from('referrals')
      .update({
        status: 'completed',
        credits_awarded: 5,
        completed_at: new Date().toISOString()
      })
      .eq('id', referral.id);

    // FIXED: Use proper SQL for incrementing
    const { data: referrerProfile } = await supabase
      .from('profiles')
      .select('total_referrals, referral_credits_earned')
      .eq('id', referrerId)
      .single();

    await supabase
      .from('profiles')
      .update({
        total_referrals: (referrerProfile?.total_referrals || 0) + 1,
        referral_credits_earned: (referrerProfile?.referral_credits_earned || 0) + 5
      })
      .eq('id', referrerId);
  }

  static async getPlans(): Promise<any[]> {
    const { data, error } = await supabase
      .from('subscription_plans')
      .select('*')
      .eq('is_active', true)
      .order('price');

    if (error) throw error;
    return data || [];
  }

  static async getUserSubscription(userId: string): Promise<any> {
    const { data, error } = await supabase
      .from('profiles')
      .select('subscription_tier, subscription_ends_at, auto_renew, credits')
      .eq('id', userId)
      .single();

    if (error) throw error;
    return data;
  }

  static async generateReferralCode(userId: string): Promise<string> {
    const code = `BB${Math.random().toString(36).substr(2, 6).toUpperCase()}`;
    
    const { error } = await supabase
      .from('profiles')
      .update({ referral_code: code })
      .eq('id', userId);

    if (error) throw error;
    return code;
  }

  static async processReferralSignup(newUserId: string, referralCode: string): Promise<void> {
    const { data: referrer, error } = await supabase
      .from('profiles')
      .select('id')
      .eq('referral_code', referralCode)
      .single();

    if (error || !referrer) return;

    await supabase
      .from('referrals')
      .insert({
        referrer_id: referrer.id,
        referred_id: newUserId,
        status: 'pending'
      });
  }
}